import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map, distinctUntilChanged, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { ApiService } from '../../api/api.service';
import { ENDPOINTS } from '../../api/endpoints';
import { PickListMap, PickListValue, DropdownOption } from '../models/picklist.model';
import { GLOBAL_PICKLIST_IDS } from '../../config/picklist.config';
import { API_HOST } from '../../api/api.config';

@Injectable({
    providedIn: 'root'
})
export class PickListService {

    private readonly STORAGE_KEY = 'APP_PICKLISTS';

    private store = new BehaviorSubject<PickListMap>(this.loadFromStorage());

    public picklists$ = this.store.asObservable();

    constructor(private api: ApiService) { }

    loadGlobalLists(): void {
        this.loadCodes(GLOBAL_PICKLIST_IDS);
    }

    loadCodes(codes: number[]): void {
        if (!codes || codes.length === 0) return;

        const ids = codes.join(',');

        this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.pickList}?grpSeqs=${ids}`)
            .subscribe({
                next: (res: any) => {
                    this.updateStore(res);
                },
                error: (err) => console.error(`Failed to load picklists: ${ids}`, err)
            });
    }

    /**
     * Same fetch as loadCodes, but returns an Observable that completes once
     * the store has actually been updated with this batch's response -
     * callers that need a deterministic "loaded" signal (rather than
     * reactively inferring it from picklists$, which can emit partial or
     * stale-cached states) should use this instead.
     */
    loadCodesAsync(codes: number[]): Observable<PickListMap> {
        if (!codes || codes.length === 0) {
            return of(this.store.value);
        }

        const ids = codes.join(',');

        return this.api.get(API_HOST.setup, `${ENDPOINTS.SETUP.pickList}?grpSeqs=${ids}`)
            .pipe(
                tap((res: any) => this.updateStore(res)),
                map(() => this.store.value),
            );
    }

    getOptions(groupCode: number): Observable<DropdownOption[]> {
        return this.picklists$.pipe(
            map(data => data[groupCode] || []),
            map(items => this.transformToOptions(items)),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr))
        );
    }

    getOptionsSync(groupCode: number): DropdownOption[] {
        const data = this.store.value;
        const items = data[groupCode] || [];
        return this.transformToOptions(items);
    }

    // ================= PRIVATE HELPERS =================

    private loadFromStorage(): PickListMap {
        const raw = localStorage.getItem(this.STORAGE_KEY);
        if (!raw) return {};
        try {
            return JSON.parse(raw);
        } catch (e) {
            console.error('Error parsing picklists from storage', e);
            return {};
        }
    }

    private updateStore(newBatch: PickListMap): void {
        const current = this.store.value;
        const updated = { ...current, ...newBatch };

        localStorage.setItem(this.STORAGE_KEY, JSON.stringify(updated));

        this.store.next(updated);
    }

    private transformToOptions(items: PickListValue[]): DropdownOption[] {
        if (!Array.isArray(items)) return [];
        return items.map(item => ({
            label: item.refCdDscr,
            value: item.refCdSeq,
            original: item
        }));
    }
}